import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { getEstimatesFromDB } from '../../../services/dataService';
import { getTemplates } from '../../../utils/estimateStorage';
import { exportComparisonPDF } from '../../../utils/scenarioExportService';
import { useAuth } from '../../../contexts/AuthContext';

// ─── helpers ────────────────────────────────────────────────────────────────

const fmt = (v) => `OMR ${parseFloat(v || 0)?.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`;
const fmtPct = (v) => `${parseFloat(v || 0)?.toFixed(2)}%`;

const COMPARISON_ROWS = [
  { key: 'sellPrice',              label: 'Sell Price',           format: fmt,    highlight: true },
  { key: 'totalCost',              label: 'Total Cost',           format: fmt,    highlight: true },
  { key: 'grossProfit',            label: 'Gross Profit',         format: fmt,    highlight: true },
  { key: 'profitMarginPercentage', label: 'Profit Margin',        format: fmtPct, highlight: true },
  { key: 'directCosts',            label: 'Direct Costs',         format: fmt,    highlight: false },
  { key: 'totalEngineerCosts',     label: 'Engineer Costs',       format: fmt,    highlight: false },
  { key: 'totalMaterialsCost',     label: 'Materials Cost',       format: fmt,    highlight: false },
  { key: 'totalLicensesCosts',     label: 'Licenses Cost',        format: fmt,    highlight: false },
  { key: 'totalOutsourceCosts',    label: 'Outsource Cost',       format: fmt,    highlight: false },
  { key: 'overheadCosts',          label: 'Overhead',             format: fmt,    highlight: false },
  { key: 'contingencyCosts',       label: 'Contingency',          format: fmt,    highlight: false },
  { key: 'overheadPercentage',     label: 'Overhead %',           format: fmtPct, highlight: false },
  { key: 'contingencyPercentage',  label: 'Contingency %',        format: fmtPct, highlight: false },
  { key: 'marginPercentage',       label: 'Target Margin %',      format: fmtPct, highlight: false },
];

const calculateTemplateCalc = (config) => {
  const modeMultiplier = { onsite: 1.0, remote: 0.85, hybrid: 0.92 }?.[config?.deliveryMode] || 1.0;
  const totalEngineerCosts = config?.selectedEngineers?.reduce((s, e) =>
    s + (e?.count * e?.hours * e?.costPerHour * modeMultiplier), 0) || 0;
  const totalMaterialsCost = config?.selectedMaterials?.reduce((s, m) => {
    const base = m?.quantity * m?.unitCost;
    return s + base + base * (m?.wastePercentage / 100);
  }, 0) || 0;
  const totalLicensesCosts = config?.selectedLicenses?.reduce((s, l) =>
    l?.type === 'perpetual'
      ? s + l?.oneTimeCost * l?.quantity
      : s + l?.monthlyCost * l?.quantity * l?.duration, 0) || 0;
  const directCosts = totalEngineerCosts + totalMaterialsCost + totalLicensesCosts;
  const overheadCosts = directCosts * ((config?.config?.overhead || 15) / 100);
  const contingencyCosts = (directCosts + overheadCosts) * ((config?.config?.contingency || 8) / 100);
  const totalCost = directCosts + overheadCosts + contingencyCosts;
  const sellPrice = totalCost / (1 - (config?.config?.targetMargin || 25) / 100);
  const grossProfit = sellPrice - totalCost;
  return {
    totalEngineerCosts, totalMaterialsCost, totalLicensesCosts,
    totalOutsourceCosts: 0, directCosts, overheadCosts, contingencyCosts,
    totalCost, sellPrice, grossProfit,
    profitMarginPercentage: (grossProfit / sellPrice) * 100,
    overheadPercentage: config?.config?.overhead || 15,
    contingencyPercentage: config?.config?.contingency || 8,
    marginPercentage: config?.config?.targetMargin || 25,
  };
};

const calcRisk = (c) => {
  let score = 0; const factors = [];
  if ((c?.profitMarginPercentage || 0) < 15) { score += 30; factors?.push('Low profit margin'); }
  else if ((c?.profitMarginPercentage || 0) < 20) { score += 15; factors?.push('Moderate profit margin'); }
  if ((c?.contingencyPercentage || 0) < 5) { score += 20; factors?.push('Low contingency buffer'); }
  if (c?.directCosts / (c?.totalCost || 1) > 0.85) { score += 25; factors?.push('High direct cost ratio'); }
  if ((c?.totalCost || 0) > 500000) { score += 15; factors?.push('High project value'); }
  if (c?.overheadCosts / (c?.directCosts || 1) < 0.1) { score += 10; factors?.push('Low overhead allocation'); }
  return { score: Math.min(score, 100), factors };
};

const riskLevel = (score) => {
  if (score >= 70) return { label: 'High Risk',   color: 'text-red-500',    bg: 'bg-red-50',    border: 'border-red-200' };
  if (score >= 40) return { label: 'Medium Risk', color: 'text-amber-500',  bg: 'bg-amber-50',  border: 'border-amber-200' };
  return              { label: 'Low Risk',    color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' };
};

// Returns css class for a cell value relative to min/max across items
const diffClass = (value, values, rowKey) => {
  const nums = values?.filter(v => typeof v === 'number' && !isNaN(v));
  if (nums?.length < 2) return '';
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  if (min === max) return '';
  // For profit/margin: higher = better (green); for costs: lower = better (green)
  const higherIsBetter = ['sellPrice', 'grossProfit', 'profitMarginPercentage']?.includes(rowKey);
  if (higherIsBetter) {
    if (value === max) return 'bg-emerald-50 text-emerald-700 font-semibold';
    if (value === min) return 'bg-red-50 text-red-600';
  } else {
    if (value === min) return 'bg-emerald-50 text-emerald-700 font-semibold';
    if (value === max) return 'bg-red-50 text-red-600';
  }
  return '';
};

// ─── Main Component ──────────────────────────────────────────────────────────

const ComparisonView = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState('estimates');
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);

  // ── load items ──────────────────────────────────────────────────────────────
  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      if (viewMode === 'estimates') {
        const data = await getEstimatesFromDB();
        setAllItems(data || []);
      } else {
        setAllItems(getTemplates() || []);
      }
    } catch (e) {
      console.error('ComparisonView load error:', e);
    } finally {
      setLoading(false);
    }
  }, [viewMode]);

  useEffect(() => {
    if (isOpen) {
      loadItems();
      setSelectedItems([]);
      setComparisonData(null);
      setSearchQuery('');
    }
  }, [isOpen, viewMode]);

  // ── search ──────────────────────────────────────────────────────────────────
  const filteredItems = searchQuery?.trim()
    ? allItems?.filter(i =>
        (i?.name || '')?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        (i?.projectName || '')?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      )
    : allItems;

  // ── selection ───────────────────────────────────────────────────────────────
  const toggleItem = (item) => {
    setSelectedItems(prev => {
      const exists = prev?.find(i => i?.id === item?.id);
      if (exists) return prev?.filter(i => i?.id !== item?.id);
      if (prev?.length >= 4) { alert('Maximum 4 items can be compared at once'); return prev; }
      return [...prev, item];
    });
  };

  // ── build comparison ────────────────────────────────────────────────────────
  const buildComparison = () => {
    if (selectedItems?.length < 2) { alert('Please select at least 2 items to compare'); return; }
    const enriched = selectedItems?.map(item => {
      const calc = viewMode === 'estimates'
        ? (item?.calculations || {})
        : calculateTemplateCalc(item?.config || item);
      return { ...item, calc, risk: calcRisk(calc) };
    });
    setComparisonData(enriched);
  };

  const handleExport = () => {
    if (!comparisonData) return;
    exportComparisonPDF({ items: comparisonData, averages: buildAverages(comparisonData) }, viewMode);
  };

  const buildAverages = (items) => {
    const avg = (key) => items?.reduce((s, i) => s + (i?.calc?.[key] || 0), 0) / items?.length;
    return { sellPrice: avg('sellPrice'), totalCost: avg('totalCost'), grossProfit: avg('grossProfit') };
  };

  if (!isOpen) return null;

  // ── SELECTION PANEL ─────────────────────────────────────────────────────────
  const SelectionPanel = () => (
    <div className="flex-1 overflow-y-auto p-6 space-y-5">
      {/* Search */}
      <Input
        placeholder={`Search ${viewMode}…`}
        value={searchQuery}
        onChange={e => setSearchQuery(e?.target?.value)}
        iconName="Search"
      />

      {/* Selection counter */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {selectedItems?.length} selected &nbsp;·&nbsp; select 2–4 to compare
        </p>
        {selectedItems?.length >= 2 && (
          <Button size="sm" variant="default" iconName="GitCompare" iconPosition="left" onClick={buildComparison}>
            Compare Now
          </Button>
        )}
      </div>

      {/* Grid of selectable cards */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : filteredItems?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="p-6 rounded-2xl bg-muted mb-4">
            <Icon name="GitCompare" size={40} color="var(--color-muted-foreground)" />
          </div>
          <p className="text-sm text-muted-foreground">
            {searchQuery ? 'No results found' : `No saved ${viewMode} yet`}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredItems?.map(item => {
            const isSelected = !!selectedItems?.find(i => i?.id === item?.id);
            const selIdx = selectedItems?.findIndex(i => i?.id === item?.id);
            return (
              <button
                key={item?.id}
                onClick={() => toggleItem(item)}
                className={`text-left rounded-xl border p-4 transition-all duration-200 ${
                  isSelected
                    ? 'border-primary ring-2 ring-primary/20 bg-primary/5 shadow-md'
                    : 'border-border hover:border-primary/40 hover:shadow-sm bg-card'
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{item?.name || 'Untitled'}</p>
                    {item?.projectName && (
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{item?.projectName}</p>
                    )}
                  </div>
                  <div className={`shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center text-xs font-bold transition-all ${
                    isSelected ? 'bg-primary border-primary text-white' : 'border-border'
                  }`}>
                    {isSelected ? selIdx + 1 : ''}
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{new Date(item?.timestamp || item?.createdAt || Date.now())?.toLocaleDateString()}</span>
                  {item?.calculations?.sellPrice != null && (
                    <span className="font-semibold text-primary">{fmt(item?.calculations?.sellPrice)}</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  // ── COMPARISON TABLE ────────────────────────────────────────────────────────
  const ComparisonTable = () => {
    const averages = buildAverages(comparisonData);
    const HEADER_COLORS = [
      'from-blue-600 to-blue-500',
      'from-violet-600 to-violet-500',
      'from-amber-600 to-amber-500',
      'from-emerald-600 to-emerald-500',
    ];

    return (
      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-5">
        {/* Back + Export */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Button variant="outline" size="sm" iconName="ArrowLeft" iconPosition="left" onClick={() => setComparisonData(null)}>
            Back to Selection
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground hidden sm:inline">
              Comparing {comparisonData?.length} {viewMode}
            </span>
            <Button variant="default" size="sm" iconName="Download" iconPosition="left" onClick={handleExport}>
              Export PDF
            </Button>
          </div>
        </div>
        {/* Averages banner */}
        <div className="grid grid-cols-3 gap-3 bg-gradient-to-br from-muted/60 to-muted/30 border border-border rounded-xl p-4">
          {[
            { label: 'Avg Sell Price',  value: fmt(averages?.sellPrice) },
            { label: 'Avg Total Cost',  value: fmt(averages?.totalCost) },
            { label: 'Avg Gross Profit', value: fmt(averages?.grossProfit) },
          ]?.map(({ label, value }) => (
            <div key={label} className="text-center">
              <p className="text-xs text-muted-foreground mb-1">{label}</p>
              <p className="text-sm font-bold text-foreground">{value}</p>
            </div>
          ))}
        </div>
        {/* Legend */}
        <div className="flex items-center gap-4 text-xs flex-wrap">
          <span className="text-muted-foreground font-medium">Difference legend:</span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300 inline-block" />
            <span className="text-emerald-700">Best value</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-red-100 border border-red-300 inline-block" />
            <span className="text-red-600">Worst value</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-card border border-border inline-block" />
            <span className="text-muted-foreground">No difference</span>
          </span>
        </div>
        {/* Side-by-side table */}
        <div className="overflow-x-auto rounded-xl border border-border shadow-sm">
          <table className="w-full text-sm border-collapse min-w-[480px]">
            <thead>
              <tr>
                {/* Field label column */}
                <th className="bg-muted/60 text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b border-border w-40 sticky left-0 z-10">
                  Field
                </th>
                {comparisonData?.map((item, idx) => {
                  const risk = riskLevel(item?.risk?.score);
                  return (
                    <th key={item?.id} className="border-b border-border px-4 py-3 text-left min-w-[180px]">
                      <div className={`rounded-lg bg-gradient-to-br ${HEADER_COLORS?.[idx % HEADER_COLORS?.length]} p-3 text-white`}>
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-xs font-bold opacity-70">#{idx + 1}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${risk?.bg} ${risk?.color} border ${risk?.border}`}>
                            {risk?.label}
                          </span>
                        </div>
                        <p className="font-semibold text-sm leading-tight truncate">{item?.name || 'Untitled'}</p>
                        {item?.projectName && (
                          <p className="text-xs opacity-80 truncate mt-0.5">{item?.projectName}</p>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS?.map((row, rowIdx) => {
                const values = comparisonData?.map(item => item?.calc?.[row?.key] ?? null);
                const numericValues = values?.map(v => (typeof v === 'number' ? v : null));
                const isDivider = row?.key === 'directCosts'; // visual section break

                return (
                  <React.Fragment key={row?.key}>
                    {isDivider && (
                      <tr>
                        <td colSpan={comparisonData?.length + 1} className="bg-muted/40 px-4 py-1.5">
                          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            Cost Breakdown
                          </span>
                        </td>
                      </tr>
                    )}
                    <tr className={rowIdx % 2 === 0 ? 'bg-card' : 'bg-muted/20'}>
                      {/* Row label */}
                      <td className="px-4 py-3 text-xs font-medium text-muted-foreground border-r border-border sticky left-0 bg-inherit z-10 whitespace-nowrap">
                        {row?.label}
                      </td>
                      {/* Value cells */}
                      {comparisonData?.map((item, colIdx) => {
                        const raw = item?.calc?.[row?.key] ?? null;
                        const cellClass = row?.highlight && raw !== null
                          ? diffClass(raw, numericValues?.filter(v => v !== null), row?.key)
                          : '';
                        return (
                          <td
                            key={item?.id}
                            className={`px-4 py-3 text-sm border-r border-border/50 last:border-r-0 transition-colors ${cellClass}`}
                          >
                            {raw !== null ? row?.format(raw) : <span className="text-muted-foreground/40">—</span>}
                          </td>
                        );
                      })}
                    </tr>
                  </React.Fragment>
                );
              })}

              {/* Risk score row */}
              <tr className="bg-muted/30">
                <td className="px-4 py-3 text-xs font-medium text-muted-foreground border-r border-border sticky left-0 bg-muted/30 z-10 whitespace-nowrap">
                  Risk Score
                </td>
                {comparisonData?.map((item) => {
                  const risk = riskLevel(item?.risk?.score);
                  return (
                    <td key={item?.id} className="px-4 py-3 border-r border-border/50 last:border-r-0">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${risk?.bg} ${risk?.color} border ${risk?.border}`}>
                        <Icon name="AlertTriangle" size={11} />
                        {risk?.label} ({item?.risk?.score})
                      </span>
                    </td>
                  );
                })}
              </tr>

              {/* Delivery mode row */}
              <tr className="bg-card">
                <td className="px-4 py-3 text-xs font-medium text-muted-foreground border-r border-border sticky left-0 bg-card z-10 whitespace-nowrap">
                  Delivery Mode
                </td>
                {comparisonData?.map((item) => (
                  <td key={item?.id} className="px-4 py-3 text-sm capitalize border-r border-border/50 last:border-r-0">
                    {item?.deliveryMode || item?.config?.deliveryMode || '—'}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        {/* Risk factors */}
        {comparisonData?.some(i => i?.risk?.factors?.length > 0) && (
          <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${comparisonData?.length}, minmax(0, 1fr))` }}>
            {comparisonData?.map((item, idx) => {
              const risk = riskLevel(item?.risk?.score);
              if (!item?.risk?.factors?.length) return <div key={item?.id} />;
              return (
                <div key={item?.id} className={`rounded-xl border p-3 ${risk?.bg} ${risk?.border}`}>
                  <p className={`text-xs font-semibold mb-2 ${risk?.color}`}>#{idx + 1} Risk Factors</p>
                  <ul className="space-y-1">
                    {item?.risk?.factors?.map((f, fi) => (
                      <li key={fi} className="text-xs text-muted-foreground flex items-start gap-1.5">
                        <span className="text-red-400 mt-0.5">•</span>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
        {/* Info note */}
        <div className="flex items-start gap-3 bg-primary/5 border border-primary/20 rounded-xl p-4">
          <Icon name="Info" size={18} color="var(--color-primary)" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            <strong className="text-foreground">Green cells</strong> indicate the best value for that field; <strong className="text-foreground">red cells</strong> indicate the worst. Risk scores consider profit margins, contingency buffers, cost ratios, and project complexity.
          </p>
        </div>
      </div>
    );
  };

  // ── RENDER ──────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-7xl max-h-[92vh] flex flex-col animate-scale-in">

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-primary to-accent shadow-lg">
              <Icon name="GitCompare" size={20} color="white" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground">Side-by-Side Comparison</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {comparisonData
                  ? `Comparing ${comparisonData?.length} ${viewMode} — row-aligned with difference highlighting`
                  : 'Select 2–4 items to compare in a parallel layout'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg transition-smooth">
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        {/* Mode tabs (only visible in selection phase) */}
        {!comparisonData && (
          <div className="flex items-center gap-2 px-5 pt-4 shrink-0">
            {['estimates', 'templates']?.map(mode => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-smooth ${
                  viewMode === mode
                    ? 'bg-primary text-white shadow-md'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {mode}
              </button>
            ))}
          </div>
        )}

        {/* Body */}
        {comparisonData ? <ComparisonTable /> : <SelectionPanel />}

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-5 border-t border-border shrink-0 flex-wrap">
          {!comparisonData && (
            <span className="text-xs text-muted-foreground">
              {selectedItems?.length} / 4 selected
            </span>
          )}
          <div className="flex items-center gap-2 ml-auto">
            {!comparisonData && selectedItems?.length >= 2 && (
              <Button variant="default" iconName="GitCompare" iconPosition="left" onClick={buildComparison}>
                Compare Selected
              </Button>
            )}
            <Button variant="outline" onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonView;